import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/i18n";
import Navbar from "@/components/Navbar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const inputCls = "w-full bg-surface border border-ink/10 px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [pwError, setPwError] = useState("");

  const saveProfile = async () => {
    const { data } = await api.patch("/account/profile", { name });
    setUser(data);
    toast.success(t("profile.saved"));
  };

  const changeLang = async (code) => {
    setLang(code);
    const { data } = await api.patch("/account/profile", { language: code });
    setUser(data);
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwError("");
    try {
      await api.post("/account/change-password", { current_password: cur, new_password: nw });
      setCur(""); setNw("");
      toast.success(t("profile.saved"));
    } catch (err) {
      setPwError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteAccount = async () => {
    await api.delete("/account");
    await logout();
    nav("/");
    toast.success("Konto gelöscht (30 Tage Karenz).");
  };

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-16 max-w-2xl mx-auto px-6 space-y-12" data-testid="profile-page">
        <h1 className="font-serif text-5xl text-ink">{t("profile.title")}</h1>

        <section className="space-y-4">
          <h2 className="text-ink-soft text-sm tracking-widest uppercase">{t("profile.account")}</h2>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="profile-name" />
          <input value={user?.email || ""} disabled className={`${inputCls} opacity-60`} data-testid="profile-email" />
          <button onClick={saveProfile} data-testid="profile-save"
            className="bg-gold text-void px-6 py-2.5 hover:bg-gold-hover transition-colors">{t("profile.save")}</button>
        </section>

        <section className="space-y-4">
          <h2 className="text-ink-soft text-sm tracking-widest uppercase">{t("profile.language")}</h2>
          <div className="flex gap-3">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => changeLang(l.code)} data-testid={`profile-lang-${l.code}`}
                className={`px-6 py-2.5 border transition-colors ${lang === l.code ? "border-gold text-gold" : "border-ink/10 text-ink-soft hover:border-gold/40"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </section>

        {user?.auth_provider === "password" && (
          <section className="space-y-4">
            <h2 className="text-ink-soft text-sm tracking-widest uppercase">{t("profile.security")}</h2>
            <form onSubmit={changePw} className="space-y-4" data-testid="profile-pw-form">
              <input type="password" placeholder={t("profile.current")} value={cur}
                onChange={(e) => setCur(e.target.value)} className={inputCls} data-testid="profile-current-pw" />
              <input type="password" minLength={8} placeholder={t("profile.new")} value={nw}
                onChange={(e) => setNw(e.target.value)} className={inputCls} data-testid="profile-new-pw" />
              {pwError && <p className="text-destructive text-sm">{pwError}</p>}
              <button type="submit" className="border border-ink/20 text-ink px-6 py-2.5 hover:border-gold hover:text-gold transition-colors"
                data-testid="profile-change-pw">{t("profile.changepw")}</button>
            </form>
          </section>
        )}

        <section className="space-y-4 border-t border-destructive/20 pt-8">
          <h2 className="text-destructive/80 text-sm tracking-widest uppercase">{t("profile.danger")}</h2>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="border border-destructive/40 text-destructive px-6 py-2.5 hover:bg-destructive/10 transition-colors"
                data-testid="profile-delete">{t("profile.deletebtn")}</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-graphite border-ink/10">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-ink font-serif">{t("profile.danger")}</AlertDialogTitle>
                <AlertDialogDescription className="text-ink-soft">{t("profile.deleteconfirm")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-ink/20 text-ink">{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} data-testid="profile-delete-confirm"
                  className="bg-destructive text-white hover:bg-destructive/80">{t("profile.deletebtn")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>
      </main>
    </div>
  );
}
